import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

/** A labeled usage progress bar. Coral only when near/over limit (never decorative). */
export function Meter({
  label,
  used,
  limit,
  unit,
}: {
  label: string;
  used: number;
  limit: number;
  unit: string;
}) {
  const ratio = Math.min(used / limit, 1);
  const pct = Math.round(ratio * 100);
  const near = ratio >= 0.85;
  return (
    <div>
      <div className="mb-1.5 flex items-baseline justify-between gap-2">
        <span className="text-[14px] text-ink">{label}</span>
        <span className="font-mono text-[12px] text-muted">
          {used.toLocaleString()}
          <span className="text-subtle"> / {limit.toLocaleString()} {unit}</span>
        </span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-paper-2 dark:bg-white/5">
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-500 ease-out-soft",
            near ? "bg-accent" : "bg-ink-strong/70 dark:bg-white/40",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/** A quiet status pill: a dot + word. Color comes from the state, used sparingly. */
export function StatusPill({
  tone,
  children,
}: {
  tone: "ok" | "pending" | "error" | "muted";
  children: ReactNode;
}) {
  const dot =
    tone === "ok"
      ? "bg-emerald-500"
      : tone === "error"
        ? "bg-accent"
        : tone === "pending"
          ? "bg-amber-500"
          : "bg-subtle";
  return (
    <span className="inline-flex items-center gap-1.5 rounded-chip bg-paper-2 px-2.5 py-1 text-[12px] text-muted dark:bg-white/5">
      <span className={cn("h-1.5 w-1.5 rounded-full", dot)} />
      {children}
    </span>
  );
}

/** Two-line "definition" row used across Profile / Plan. */
export function FieldRow({
  label,
  children,
  className,
}: {
  label: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex items-center justify-between gap-4 py-3", className)}>
      <span className="text-[13px] text-muted">{label}</span>
      <span className="text-right text-[14px] text-ink-strong">{children}</span>
    </div>
  );
}

/** Tiny mono chip marking sample / unauthenticated data so live vs sample is never ambiguous. */
export function SampleChip({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-chip bg-paper-2 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-subtle dark:bg-white/5",
        className,
      )}
      title="Sample data — not from your live workspace"
    >
      sample
    </span>
  );
}

/** A calm shimmer placeholder line, used for loading states (no layout shift). */
export function SkeletonLine({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        "inline-block animate-pulse rounded-full bg-paper-2 align-middle dark:bg-white/5",
        className,
      )}
    />
  );
}

/** Round monogram avatar. */
export function Monogram({
  initials,
  size = "md",
  className,
}: {
  initials: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const dims = { sm: "h-9 w-9 text-[13px]", md: "h-11 w-11 text-[15px]", lg: "h-14 w-14 text-[18px]" }[size];
  return (
    <span
      className={cn(
        "grid shrink-0 place-items-center rounded-full bg-ink-strong font-semibold text-canvas",
        dims,
        className,
      )}
    >
      {initials}
    </span>
  );
}
